import React from 'react';
import { Handshake, ShieldCheck, Award, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { company } from '../../mock';
import { useI18n } from '../../i18n';

const partnerT = {
  en: {
    kicker: 'Alliances & Partnerships',
    title: 'Proudly partnering with global industry leaders',
    body: 'We collaborate with visionary companies to deliver enterprise-grade digital transformation across industries.',
    authorized: 'Authorized Partner',
    hipro: 'Hipro Technologies',
    sasol: 'Sasol',
    partnershipTitle: 'Hipro Technologies × Sasol',
    partnershipBody: 'A strategic collaboration to accelerate digital transformation, cloud engineering and AI-led operations across global markets.',
    visitPartner: 'Visit Sasol',
    badges: ['Enterprise-Grade', 'ISO Certified', 'Global Delivery'],
  },
  it: {
    kicker: 'Alleanze e partnership',
    title: 'Orgogliosi di collaborare con leader globali',
    body: 'Collaboriamo con aziende visionarie per offrire trasformazione digitale enterprise in tutti i settori.',
    authorized: 'Partner Autorizzato',
    hipro: 'Hipro Technologies',
    sasol: 'Sasol',
    partnershipTitle: 'Hipro Technologies × Sasol',
    partnershipBody: 'Una collaborazione strategica per accelerare la trasformazione digitale, il cloud e le operazioni guidate da AI nei mercati globali.',
    visitPartner: 'Visita Sasol',
    badges: ['Enterprise', 'ISO Certificati', 'Delivery Globale'],
  },
  hi: {
    kicker: 'साझेदारी और गठबंधन',
    title: 'वैश्विक इंडस्ट्री लीडर्स के साथ गर्व से साझेदारी',
    body: 'हम दूरदर्शी कंपनियों के साथ मिलकर उद्योगों में एंटरप्राइज़-ग्रेड डिजिटल ट्रांसफ़ॉर्मेशन देते हैं।',
    authorized: 'अधिकृत साझेदार',
    hipro: 'Hipro Technologies',
    sasol: 'Sasol',
    partnershipTitle: 'Hipro Technologies × Sasol',
    partnershipBody: 'डिजिटल ट्रांसफ़ॉर्मेशन, क्लाउड इंजीनियरिंग और AI-आधारित संचालन को वैश्विक बाज़ारों में तेज़ करने के लिए रणनीतिक सहयोग।',
    visitPartner: 'Sasol देखें',
    badges: ['एंटरप्राइज़-ग्रेड', 'ISO प्रमाणित', 'वैश्विक डिलीवरी'],
  },
};

const Partners = () => {
  const { lang } = useI18n();
  const s = partnerT[lang] || partnerT.en;

  return (
    <section id="partners" className="py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-600">{s.kicker}</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{s.title}</h2>
          <p className="mt-4 text-slate-600">{s.body}</p>
        </div>

        <div className="mt-12 relative rounded-3xl border border-slate-200 overflow-hidden shadow-xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 text-white">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                'radial-gradient(700px 300px at 15% 20%, rgba(249,115,22,0.28), transparent 60%), radial-gradient(700px 300px at 90% 80%, rgba(59,130,246,0.28), transparent 60%)',
            }}
          />

          <div className="relative p-8 sm:p-12 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4 flex justify-center">
              <div className="rounded-2xl bg-white p-6 shadow-2xl w-full max-w-[240px] flex flex-col items-center gap-3">
                <img src={company.logo} alt="Hipro Technologies" className="h-16 w-auto" />
                <div className="text-sm font-bold text-slate-900">{s.hipro}</div>
                <div className="text-[11px] text-slate-500 text-center">USA · UK · IT · SG · IN</div>
              </div>
            </div>

            <div className="lg:col-span-4 flex flex-col items-center">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-2xl ring-4 ring-white/10">
                <Handshake className="h-10 w-10 text-white" />
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] font-bold uppercase tracking-widest px-3 py-1 border border-emerald-400/30">
                <ShieldCheck className="h-3.5 w-3.5" /> {s.authorized}
              </div>
              <div className="mt-3 text-center">
                <div className="text-lg sm:text-xl font-extrabold">{s.partnershipTitle}</div>
                <p className="mt-2 text-sm text-slate-300 max-w-sm">{s.partnershipBody}</p>
              </div>
            </div>

            <div className="lg:col-span-4 flex justify-center">
              <div className="rounded-2xl bg-white p-6 shadow-2xl w-full max-w-[240px] flex flex-col items-center gap-3">
                <div className="h-16 w-full flex items-center justify-center">
                  <img
                    src="https://logo.clearbit.com/sasol.com?size=200"
                    alt="Sasol"
                    className="max-h-14 w-auto object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fb = e.currentTarget.nextSibling;
                      if (fb) fb.style.display = 'block';
                    }}
                  />
                  <div style={{ display: 'none' }} className="text-3xl font-black tracking-tight">
                    <span className="text-[#0069b4]">sa</span>
                    <span className="text-[#ed1c24]">s</span>
                    <span className="text-[#0069b4]">ol</span>
                  </div>
                </div>
                <div className="text-sm font-bold text-slate-900">{s.sasol}</div>
                <a
                  href="https://sasol.com/"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-[11px] text-orange-600 hover:text-orange-700 font-semibold inline-flex items-center gap-1"
                >
                  sasol.com <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>

          <div className="relative px-8 sm:px-12 pb-8 flex flex-wrap justify-center gap-3">
            {s.badges.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-200 bg-white/5 border border-white/10 rounded-full px-3 py-1">
                <Award className="h-3.5 w-3.5 text-orange-400" /> {b}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Partners;
